import { ChangeDetectorRef, Component, Input, NgZone, OnDestroy } from '@angular/core';
import { NgIf, NgFor, AsyncPipe, CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  BehaviorSubject,
  Subject,
  Observable,
  combineLatest,
  merge,
  of,
  Subscription,
  timer,
} from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
  exhaustMap,
  delay,
} from 'rxjs/operators';

import { FreshdeskSolutionViewerComponent } from '../freshdesk-solution-viewer/freshdesk-solution-viewer.component';
import { FreshdeskSolutionService } from '../../services/freshdesk-solutions.service';
import { JobLogPatternService } from '@asamco/application-link/shared';

type IssueArticleVm = {
  articleId: number;
  title: string;
  bodyHtml: string;
};

type FreshdeskArticleApiModel = {
  articleId?: number;
  ArticleId?: number;
  id?: number;
  Id?: number;
  title?: string;
  Title?: string;
  bodyHtml?: string;
  BodyHtml?: string;
  descriptionHtml?: string;
  DescriptionHtml?: string;
};

type SupportReason = 'not_found' | 'unclear' | 'outdated' | 'wrong' | 'other';
type OpenReq = { title: string; articleIds: number[]; origin?: HelpOrigin } | null;

type SupportDiagnostics = {
  url?: string;
  userAgent?: string;
  timezone?: string;
  locale?: string;
  timestampIso?: string;
};

type HelpOrigin =
  | {
    kind: 'link_logs';
    linkId: number;
    pattern?: {
      id?: number | string | null;
      message?: string | null;
    };
    logMessage?: string | null;
  }
  | {
    kind: 'link_settings';
    linkId?: number | string | null;
    setting: {
      id?: number | string | null;
      name?: string | null;
    };
  }
  | {
    kind: 'link_support';
    linkId?: number | string | null;
  }
  | {
    kind: 'application_login';
    linkId?: number | string | null;
    appSide?: string | null;
    applicationId?: number | string | null;
    loginName?: string | null;
  }
  | {
    kind: 'pattern_admin';
    pattern?: {
      id?: number | string | null;
      title?: string | null;
    };
    logMessage?: string | null;
  }
  | { kind: 'unknown' };

type SupportPayload = {
  articleId: number;
  rating: number | null;
  reason: string;
  details: string;
  email?: string | null;
  diagnostics?: SupportDiagnostics;
  origin?: HelpOrigin;
};

type SupportUiState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'sent' }
  | { kind: 'error'; message: string };

@Component({
  selector: 'al-freshdesk-help-modal',
  standalone: true,
  imports: [NgIf, NgFor, AsyncPipe, FormsModule, FreshdeskSolutionViewerComponent, CommonModule],
  templateUrl: './freshdesk-help-modal.component.html',
  styleUrls: ['./freshdesk-help-modal.component.scss'],
})
export class FreshdeskHelpModalComponent implements OnDestroy {
  @Input() backdrop = true;
  @Input() isAdmin = false;

  private readonly destroy$ = new Subject<void>();
  private readonly subs = new Subscription();

  private readonly openReq$ = new BehaviorSubject<OpenReq>(null);
  private readonly searchTerm$ = new BehaviorSubject<string>('');

  issueSearchTerm = '';

  private readonly issueLoadingSubject = new BehaviorSubject<boolean>(false);
  readonly issueLoading$ = this.issueLoadingSubject.asObservable().pipe(shareReplay(1));

  readonly visible$: Observable<boolean> = this.openReq$.pipe(
    map(req => !!req),
    distinctUntilChanged(),
    shareReplay(1)
  );

  readonly headerTitle$: Observable<string> = this.openReq$.pipe(
    map(req => req?.title || 'Help'),
    distinctUntilChanged(),
    shareReplay(1)
  );

  private readonly selectedArticleIdSubject = new BehaviorSubject<number | null>(null);

  private readonly ratingsByArticleId = new Map<number, number>();
  issueSelectedRating: number | null = null;
  hoverRating: number | null = null;

  private currentSelectedId: number | null = null;

  private readonly supportVisibleSubject = new BehaviorSubject<boolean>(false);
  readonly isSupportVisible$ = this.supportVisibleSubject.asObservable().pipe(
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  supportReason: SupportReason = 'not_found';
  supportDetails = '';
  supportEmail: string | null = null;
  includeDiagnostics = true;

  supportSubmitting = false;
  supportSent = false;
  supportError = false;
  supportErrorMessage: string | null = null;

  private readonly supportSubmitClick$ = new Subject<void>();
  private readonly supportValidationError$ = new Subject<string>();

  private readonly supportReasonLabels: Record<SupportReason, string> = {
    not_found: "Article doesn't cover my issue",
    unclear: "Instructions are unclear",
    outdated: "Looks outdated",
    wrong: "Seems incorrect",
    other: 'Other',
  };

  readonly supportUiState$: Observable<SupportUiState> = merge(
    this.supportSubmitClick$.pipe(
      filter(() => this.supportVisibleSubject.getValue()),
      map(() => this.buildPayloadOrEmitValidationError()),
      filter((x): x is SupportPayload => x !== null),
      exhaustMap(payload =>
        merge(
          of<SupportUiState>({ kind: 'submitting' }),
          this.freshdeskSolutionService.submitSupport(payload).pipe(
            map((): SupportUiState => ({ kind: 'sent' })),
            catchError(err => {
              console.error('submitSupport failed', err);
              return of<SupportUiState>({
                kind: 'error',
                message: 'Couldn’t send. Please try again.',
              });
            })
          )
        )
      )
    ),
    this.supportValidationError$.pipe(
      map(message => ({ kind: 'error', message } as SupportUiState))
    )
  ).pipe(
    startWith<SupportUiState>({ kind: 'idle' }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private readonly autoCloseSeconds = 4;

  readonly supportAutoCloseIn$ = this.supportUiState$.pipe(
    switchMap(s => {
      if (s.kind !== 'sent') return of<number | null>(null);

      return timer(0, 1000).pipe(
        map(t => this.autoCloseSeconds - t),
        take(this.autoCloseSeconds + 1)
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly supportSubmitting2$ = this.supportUiState$.pipe(
    map(s => s.kind === 'submitting'),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly supportSent2$ = this.supportUiState$.pipe(
    map(s => s.kind === 'sent'),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly supportError2$ = this.supportUiState$.pipe(
    map(s => s.kind === 'error'),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly supportErrorMessage2$ = this.supportUiState$.pipe(
    map(s => (s.kind === 'error' ? s.message : null)),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private readonly supportUiStateSyncSub = this.supportUiState$
    .pipe(takeUntil(this.destroy$))
    .subscribe(state => {
      this.supportSubmitting = false;
      this.supportSent = false;
      this.supportError = false;
      this.supportErrorMessage = null;

      if (state.kind === 'submitting') {
        this.supportSubmitting = true;
      } else if (state.kind === 'sent') {
        this.supportSent = true;
      } else if (state.kind === 'error') {
        this.supportError = true;
        this.supportErrorMessage = state.message;
      }
    });

  private readonly autoCloseOnSentSub = this.supportUiState$
    .pipe(
      filter(s => s.kind === 'sent'),
      tap(() => {
        this.supportDetails = '';
        this.supportEmail = null;
        this.supportReason = 'not_found';
        this.includeDiagnostics = true;
        this.supportError = false;
        this.supportErrorMessage = null;
        this.cdr.detectChanges();
      }),
      delay(this.autoCloseSeconds * 1000),
      takeUntil(this.destroy$)
    )
    .subscribe(() => {
      this.ngZone.run(() => {
        this.closeSupport();
        this.cdr.detectChanges();
      });
    });

  private mapApiArticleToVm(a: FreshdeskArticleApiModel | null | undefined): IssueArticleVm | null {
    if (!a) return null;

    const articleId = Number(a.articleId ?? a.ArticleId ?? a.id ?? a.Id);
    if (!Number.isFinite(articleId) || articleId <= 0) return null;

    return {
      articleId,
      title: a.title ?? a.Title ?? '',
      bodyHtml: a.bodyHtml ?? a.BodyHtml ?? a.descriptionHtml ?? a.DescriptionHtml ?? '',
    };
  }

  private readonly refreshedOverridesSubject = new BehaviorSubject<IssueArticleVm[]>([]);
  private readonly refreshedOverrides$ = this.refreshedOverridesSubject.asObservable();

  private readonly refreshingArticleIdsSubject = new BehaviorSubject<Set<number>>(new Set<number>());
  readonly refreshingArticleIds$ = this.refreshingArticleIdsSubject.asObservable().pipe(shareReplay(1));

  private readonly baseArticles$: Observable<IssueArticleVm[]> = this.openReq$.pipe(
    switchMap(req => {
      const ids = req?.articleIds ?? [];

      this.selectedArticleIdSubject.next(null);
      this.issueSearchTerm = '';
      this.searchTerm$.next('');
      this.resetSupportState(true);
      this.issueSelectedRating = null;
      this.hoverRating = null;

      if (!ids.length) {
        this.issueLoadingSubject.next(false);
        return of([] as IssueArticleVm[]);
      }

      this.issueLoadingSubject.next(true);

      return this.freshdeskSolutionService.getArticlesByIds(ids).pipe(
        map((articles: any[]) =>
          (articles ?? [])
            .map(a => this.mapApiArticleToVm(a as FreshdeskArticleApiModel))
            .filter((x): x is IssueArticleVm => x !== null)
        ),
        switchMap((vms: IssueArticleVm[]) => {
          const articleIds = (vms ?? []).map(x => x.articleId).filter(Boolean);

          this.ratingsByArticleId.clear();

          if (!articleIds.length) return of(vms);

          return this.freshdeskSolutionService.getMyRatingsByIds(articleIds).pipe(
            tap(dict => {
              Object.keys(dict ?? {}).forEach(k => {
                const id = Number(k);
                const rating = Number((dict as any)[k]);
                if (id > 0 && rating >= 1 && rating <= 5) {
                  this.ratingsByArticleId.set(id, rating);
                }
              });
            }),
            map(() => vms),
            catchError(err => {
              console.error('FreshdeskHelpModal: getMyRatingsByIds failed', err);
              return of(vms);
            })
          );
        }),
        catchError(err => {
          console.error('FreshdeskHelpModal: getArticlesByIds failed', err);
          return of([] as IssueArticleVm[]);
        }),
        finalize(() => this.issueLoadingSubject.next(false))
      );
    }),
    shareReplay(1)
  );

  private readonly appendedArticlesSubject = new BehaviorSubject<IssueArticleVm[]>([]);
  private readonly appendedArticles$ = this.appendedArticlesSubject.asObservable();

  readonly issueArticlesAll$: Observable<IssueArticleVm[]> = combineLatest([
    this.baseArticles$,
    this.appendedArticles$,
    this.refreshedOverrides$,
  ]).pipe(
    map(([base, extra, overrides]) => {
      const byId = new Map<number, IssueArticleVm>();
      (base ?? []).forEach(a => byId.set(a.articleId, a));
      (extra ?? []).forEach(a => byId.set(a.articleId, a));
      (overrides ?? []).forEach(a => byId.set(a.articleId, a));
      return Array.from(byId.values());
    }),
    shareReplay(1)
  );

  readonly filteredIssueArticlesAll$: Observable<IssueArticleVm[]> = combineLatest([
    this.issueArticlesAll$,
    this.searchTerm$.pipe(startWith('')),
  ]).pipe(
    map(([list, term]) => {
      const q = (term || '').trim().toLowerCase();
      if (!q) return list ?? [];
      return (list ?? []).filter(a => (a?.title || '').toLowerCase().includes(q));
    }),
    shareReplay(1)
  );

  readonly issueSelectedArticleAll$: Observable<IssueArticleVm | null> = combineLatest([
    this.issueArticlesAll$,
    this.selectedArticleIdSubject.pipe(startWith(null)),
  ]).pipe(
    map(([articles, selectedId]) => {
      if (!articles?.length) return null;
      if (selectedId == null) return articles[0] ?? null;
      return articles.find(a => a.articleId === selectedId) ?? (articles[0] ?? null);
    }),
    tap(article => this.syncSelectedRating(article)),
    shareReplay(1)
  );

  private readonly ratingSaveClick$ = new Subject<{ articleId: number; rating: number }>();
  private readonly savingRatingByArticleId = new Set<number>();

  private readonly ratingSaveSub = this.ratingSaveClick$
    .pipe(
      filter(({ articleId }) => !this.savingRatingByArticleId.has(articleId)),
      tap(({ articleId }) => this.savingRatingByArticleId.add(articleId)),
      exhaustMap(({ articleId, rating }) =>
        this.freshdeskSolutionService.upsertMyRating(articleId, rating).pipe(
          tap(saved => {
            if (typeof saved === 'number' && saved >= 1 && saved <= 5) {
              this.ratingsByArticleId.set(articleId, saved);
              if (this.currentSelectedId === articleId) {
                this.issueSelectedRating = saved;
              }
            }
          }),
          catchError(err => {
            console.error('upsertMyRating failed', err);
            return of(null);
          }),
          finalize(() => this.savingRatingByArticleId.delete(articleId))
        )
      ),
      takeUntil(this.destroy$)
    )
    .subscribe();

  private readonly ratingClearClick$ = new Subject<{ articleId: number }>();
  private readonly clearingRatingByArticleId = new Set<number>();

  private readonly ratingClearSub = this.ratingClearClick$
    .pipe(
      filter(({ articleId }) => !this.clearingRatingByArticleId.has(articleId)),
      tap(({ articleId }) => this.clearingRatingByArticleId.add(articleId)),
      exhaustMap(({ articleId }) =>
        this.freshdeskSolutionService.deleteMyRating(articleId).pipe(
          tap(() => {
            this.ratingsByArticleId.delete(articleId);
            if (this.currentSelectedId === articleId) {
              this.issueSelectedRating = null;
            }
          }),
          catchError(err => {
            console.error('deleteMyRating failed', err);
            return of(null);
          }),
          finalize(() => this.clearingRatingByArticleId.delete(articleId))
        )
      ),
      takeUntil(this.destroy$)
    )
    .subscribe();

  constructor(
    private readonly freshdeskSolutionService: FreshdeskSolutionService,
    private readonly ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef,
    private readonly jobLogPatternService: JobLogPatternService
  ) {
    this.subs.add(this.supportUiStateSyncSub);
    this.subs.add(this.autoCloseOnSentSub);
    this.subs.add(this.ratingSaveSub);
    this.subs.add(this.ratingClearSub);
  }

  open(title: string, articleIds: number[], origin?: HelpOrigin) {
    const ids = articleIds ?? [];

    if (this.backdrop && !ids.length) return;

    this.appendedArticlesSubject.next([]);
    this.refreshedOverridesSubject.next([]);
    this.selectedArticleIdSubject.next(null);

    this.issueSearchTerm = '';
    this.searchTerm$.next('');

    this.issueSelectedRating = null;
    this.hoverRating = null;
    this.currentSelectedId = null;

    this.resetSupportState(true);
    this.refreshingArticleIdsSubject.next(new Set<number>());

    this.openReq$.next({
      title: title || 'Help',
      articleIds: ids,
      origin: origin ?? { kind: 'unknown' },
    });

    if (!ids.length) this.issueLoadingSubject.next(false);
  }

  close() {
    this.openReq$.next(null);
    this.issueLoadingSubject.next(false);
    this.selectedArticleIdSubject.next(null);

    this.issueSearchTerm = '';
    this.searchTerm$.next('');

    this.appendedArticlesSubject.next([]);
    this.refreshedOverridesSubject.next([]);
    this.refreshingArticleIdsSubject.next(new Set<number>());

    this.issueSelectedRating = null;
    this.hoverRating = null;
    this.currentSelectedId = null;

    this.resetSupportState(true);
  }

  onSearchTermChanged(v: string) {
    this.issueSearchTerm = v ?? '';
    this.searchTerm$.next(this.issueSearchTerm);
  }

  selectIssueArticle(article: IssueArticleVm) {
    if (!article?.articleId) return;
    this.selectedArticleIdSubject.next(article.articleId);
  }

  clearIssueArticleRating() {
    const id = this.currentSelectedId ?? this.selectedArticleIdSubject.getValue();
    if (!id) return;

    this.ratingsByArticleId.delete(id);
    this.issueSelectedRating = null;
    this.ratingClearClick$.next({ articleId: id });
  }

  loadIssueArticleById(id: string) {
    const articleId = Number(id);
    if (!articleId) return;

    this.issueArticlesAll$.pipe(take(1), takeUntil(this.destroy$)).subscribe(list => {
      const found = (list ?? []).find(a => a.articleId === articleId);
      if (found) {
        this.selectedArticleIdSubject.next(articleId);
      } else {
        this.appendArticleById(articleId);
      }
    });
  }

  private appendArticleById(articleId: number) {
    this.issueLoadingSubject.next(true);

    this.freshdeskSolutionService
      .getArticlesByIds([articleId])
      .pipe(
        map((articles: any[]) => {
          const first = ((articles ?? [])[0] ?? null) as FreshdeskArticleApiModel | null;
          return this.mapApiArticleToVm(first);
        }),
        catchError(err => {
          console.error('FreshdeskHelpModal: append article failed', err);
          return of(null);
        }),
        finalize(() => this.issueLoadingSubject.next(false)),
        takeUntil(this.destroy$)
      )
      .subscribe(vm => {
        if (!vm) return;

        const curr = this.appendedArticlesSubject.getValue();
        if (!curr.some(x => x.articleId === vm.articleId)) {
          this.appendedArticlesSubject.next([...curr, vm]);
        }

        if (!this.ratingsByArticleId.has(vm.articleId)) {
          this.freshdeskSolutionService
            .getMyRatingsByIds([vm.articleId])
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe(dict => {
              const rating = Number((dict as any)?.[String(vm.articleId)]);
              if (rating >= 1 && rating <= 5) {
                this.ratingsByArticleId.set(vm.articleId, rating);
              }
              if (this.currentSelectedId === vm.articleId) {
                this.issueSelectedRating = this.ratingsByArticleId.get(vm.articleId) ?? null;
              }
            });
        }

        this.selectedArticleIdSubject.next(vm.articleId);
      });
  }

  rateIssueArticle(stars: number) {
    const id = this.currentSelectedId ?? this.selectedArticleIdSubject.getValue();
    if (!id) return;

    const rating = Math.max(1, Math.min(5, Number(stars)));
    this.ratingsByArticleId.set(id, rating);
    this.issueSelectedRating = rating;
    this.ratingSaveClick$.next({ articleId: id, rating });
  }

  private syncSelectedRating(article: IssueArticleVm | null) {
    const id = article?.articleId ?? null;
    this.currentSelectedId = id;
    this.issueSelectedRating = id ? (this.ratingsByArticleId.get(id) ?? null) : null;
  }

  setHoverRating(v: number) {
    this.hoverRating = v;
  }

  clearHoverRating() {
    this.hoverRating = null;
  }

  openSupportForArticle() {
    this.supportVisibleSubject.next(true);
    this.supportSubmitting = false;
    this.supportSent = false;
    this.supportError = false;
    this.supportErrorMessage = null;
  }

  closeSupport() {
    this.supportVisibleSubject.next(false);
    this.supportSubmitting = false;
  }

  get supportDetailsRequired(): boolean {
    return this.supportReason === 'other';
  }

  get supportCanSubmit(): boolean {
    if (this.supportSubmitting) return false;

    const details = (this.supportDetails ?? '').trim();
    if (this.supportDetailsRequired && !details) return false;

    const email = (this.supportEmail ?? '').trim();
    if (email && !this.isValidEmail(email)) return false;

    return !!this.currentSelectedId;
  }

  submitSupport() {
    this.supportSubmitClick$.next();
  }

  private buildPayloadOrEmitValidationError(): SupportPayload | null {
    if (!this.currentSelectedId) {
      this.supportValidationError$.next('No article selected.');
      return null;
    }

    const details = (this.supportDetails ?? '').trim();
    if (this.supportReason === 'other' && !details) {
      this.supportValidationError$.next('Please add extra details for “Other”.');
      return null;
    }

    const email = (this.supportEmail ?? '').trim();
    if (email && !this.isValidEmail(email)) {
      this.supportValidationError$.next('Please enter a valid email address (or leave it blank).');
      return null;
    }

    const origin = this.openReq$.getValue()?.origin ?? { kind: 'unknown' as const };

    return {
      articleId: this.currentSelectedId,
      rating: this.issueSelectedRating,
      reason: this.supportReasonLabels[this.supportReason],
      details,
      email: email || null,
      diagnostics: this.includeDiagnostics ? this.buildDiagnostics() : undefined,
      origin,
    };
  }

  private buildDiagnostics(): SupportDiagnostics {
    const hasWindow = typeof window !== 'undefined';
    const hasNavigator = typeof navigator !== 'undefined';

    return {
      url: hasWindow ? window.location?.href : undefined,
      userAgent: hasNavigator ? navigator.userAgent : undefined,
      timezone: Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone,
      locale: hasNavigator ? navigator.language : undefined,
      timestampIso: new Date().toISOString(),
    };
  }

  private isValidEmail(value: string): boolean {
    const v = (value || '').trim();
    if (!v) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  }

  onRatingKeydown(e: KeyboardEvent, current: number) {
    const key = e.key;

    if (key === 'ArrowLeft' || key === 'ArrowDown') {
      e.preventDefault();
      const next = Math.max(1, current - 1);
      this.setHoverRating(next);
      this.rateIssueArticle(next);
      return;
    }

    if (key === 'ArrowRight' || key === 'ArrowUp') {
      e.preventDefault();
      const next = Math.min(5, current + 1);
      this.setHoverRating(next);
      this.rateIssueArticle(next);
      return;
    }

    if (key === 'Enter' || key === ' ') {
      e.preventDefault();
      this.rateIssueArticle(current);
    }
  }

  isRefreshingArticle(articleId: number): boolean {
    return this.refreshingArticleIdsSubject.getValue().has(articleId);
  }

  refreshIssueArticle(article: IssueArticleVm): void {
    if (!this.isAdmin) return;
    if (!article?.articleId) return;

    const refresh$ = this.getRefreshRequestFromOrigin(article.articleId);
    if (!refresh$) {
      console.warn('FreshdeskHelpModal: no supported origin available for article refresh.');
      return;
    }

    const articleId = article.articleId;
    if (this.isRefreshingArticle(articleId)) return;

    this.setArticleRefreshing(articleId, true);

    refresh$
      .pipe(
        switchMap(() => this.freshdeskSolutionService.getArticlesByIds([articleId])),
        map((articles: any[]) => {
          const first = ((articles ?? [])[0] ?? null) as FreshdeskArticleApiModel | null;
          return this.mapApiArticleToVm(first);
        }),
        catchError(err => {
          console.error('FreshdeskHelpModal: refresh article failed', err);
          return of(null);
        }),
        finalize(() => this.setArticleRefreshing(articleId, false)),
        takeUntil(this.destroy$)
      )
      .subscribe(vm => {
        if (!vm) return;

        const curr = this.refreshedOverridesSubject.getValue();
        const next = [...curr.filter(x => x.articleId !== vm.articleId), vm];
        this.refreshedOverridesSubject.next(next);

        if (this.currentSelectedId === vm.articleId) {
          this.selectedArticleIdSubject.next(vm.articleId);
        }

        this.cdr.detectChanges();
      });
  }

  private getPatternIdFromOrigin(): number | null {
    const origin = this.openReq$.getValue()?.origin;
    if (!origin) return null;
    if (origin.kind !== 'pattern_admin') return null;

    const id = Number(origin.pattern?.id);
    return Number.isFinite(id) && id > 0 ? id : null;
  }

  private getRefreshRequestFromOrigin(articleId: number): Observable<unknown> | null {
    const origin = this.openReq$.getValue()?.origin;
    if (!origin) return null;

    if (origin.kind === 'pattern_admin') {
      const patternId = this.getPatternIdFromOrigin();
      return patternId ? this.jobLogPatternService.refreshArticle(patternId, articleId) : null;
    }

    if (origin.kind === 'application_login') {
      const applicationId = Number(origin.applicationId);
      return Number.isFinite(applicationId) && applicationId > 0
        ? this.freshdeskSolutionService.refreshApplicationLoginArticle(applicationId, articleId)
        : null;
    }

    return null;
  }

  private setArticleRefreshing(articleId: number, refreshing: boolean): void {
    const next = new Set(this.refreshingArticleIdsSubject.getValue());

    if (refreshing) {
      next.add(articleId);
    } else {
      next.delete(articleId);
    }

    this.refreshingArticleIdsSubject.next(next);
  }

  private resetSupportState(fullReset: boolean) {
    this.supportVisibleSubject.next(false);
    this.supportReason = 'not_found';
    this.supportDetails = '';
    this.supportSubmitting = false;

    this.supportSent = false;
    this.supportError = false;
    this.supportErrorMessage = null;

    if (fullReset) {
      this.supportEmail = null;
      this.includeDiagnostics = true;
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.subs.unsubscribe();
  }
}
